import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Droplets, Gem, ShoppingBag, HeartHandshake, ArrowRight } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { Reveal, MaskedLine, EASE, PalestineFlag } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { ProductCard } from "../components/ProductCard";
import { HERO_IMG, ARCHES_IMG, prodName } from "../data/products";

const StairBottle = ({ p, i, product, name }) => {
  const y = useTransform(p, [0, 1], [130, -i * 17]);
  const o = useTransform(p, [0, 0.35, 1], [0.25, 1, 1]);
  return (
    <motion.div style={{ y, opacity: o }} className="shrink-0 group/stair" data-testid={"family-bottle-" + i}>
      <div className="w-9 sm:w-11 md:w-14 overflow-hidden rounded-b-md rounded-t-sm border border-gold/20 group-hover/stair:border-gold/70 transition-colors duration-500">
        <img src={product.image} alt={name} className="w-full block" style={{ aspectRatio: "848/1264", objectFit: "cover" }} loading="lazy" />
      </div>
    </motion.div>
  );
};

const FamilyStaircase = () => {
  const { L, products, lang } = useStore();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  return (
    <section ref={ref} className="relative py-24 lg:py-36 overflow-hidden" data-testid="family-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
        <Reveal><p className="eyebrow">{L.family.eyebrow}</p></Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ivory mt-4">{L.family.title}</h2>
        </Reveal>
        <Reveal delay={0.16}><p className="text-sand max-w-2xl mx-auto mt-4 text-sm lg:text-base leading-relaxed">{L.family.sub}</p></Reveal>
        <div className="mt-16 flex items-end justify-center gap-2 sm:gap-2.5 md:gap-3.5 min-h-[220px] md:min-h-[300px] overflow-x-auto md:overflow-visible px-4">
          {products.slice(0, 14).map((p, i) => (
            <StairBottle key={p.slug} p={scrollYProgress} i={i} product={p} name={prodName(p, lang)} />
          ))}
        </div>
        <Reveal delay={0.1} className="mt-14">
          <Link to="/shop" data-testid="family-cta" className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-[0.7rem] tracking-[0.25em] uppercase font-semibold">
            {L.family.cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  const { L, products } = useStore();
  const featured = products.filter((p) => p.featured).slice(0, 6);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bottleY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const archesY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const whyIcons = [Droplets, Gem, ShoppingBag, HeartHandshake];

  return (
    <div className="star-lattice" data-testid="home-page">
      {/* HERO */}
      <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0" style={{ background: "radial-gradient(70% 55% at 75% 40%, rgba(212,175,55,0.10), transparent 65%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-32 pb-20 grid lg:grid-cols-2 gap-14 items-center w-full">
          <div className="order-2 lg:order-1">
            <MaskedLine delay={0.15}><span className="font-arabic text-gold text-2xl md:text-3xl" dir="rtl">{L.hero.eyebrow}</span></MaskedLine>
            <h1 className="font-display text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-7xl text-ivory mt-4" data-testid="hero-title">
              <MaskedLine delay={0.3}>{L.hero.line1}</MaskedLine>
              <MaskedLine delay={0.42}>{L.hero.line2}</MaskedLine>
              <MaskedLine delay={0.54}><span className="text-gold">{L.hero.line3}</span></MaskedLine>
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8, ease: EASE }} className="text-sand text-sm lg:text-base leading-relaxed max-w-md mt-6" data-testid="hero-sub">
              {L.hero.sub}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8, ease: EASE }} className="flex flex-wrap gap-4 mt-9">
              <Link to="/shop" data-testid="hero-cta-shop" className="btn-gold rounded-full px-8 py-4 text-[0.7rem] tracking-[0.25em] uppercase font-semibold">{L.hero.ctaShop}</Link>
              <Link to="/mission" data-testid="hero-cta-mission" className="btn-ghost rounded-full px-8 py-4 text-[0.7rem] tracking-[0.25em] uppercase">{L.hero.ctaMission}</Link>
            </motion.div>
          </div>

          <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
            <motion.div style={{ y: bottleY }} className="relative w-[270px] sm:w-[320px] lg:w-[380px]" data-testid="hero-bottle">
              <motion.div
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
                className="relative overflow-hidden arch-frame border border-gold/35 gold-ring"
              >
                <img src={HERO_IMG} alt="ARIJ MISK flacon 10 ml" className="w-full block" style={{ aspectRatio: "848/1264", objectFit: "cover" }} />
                <motion.div
                  className="absolute inset-0 pointer-events-none"
                  initial={{ x: "-160%" }}
                  animate={{ x: "160%" }}
                  transition={{ delay: 1.4, duration: 1.5, ease: "easeInOut" }}
                  style={{ background: "linear-gradient(105deg, transparent 30%, rgba(255,240,200,0.28) 50%, transparent 70%)" }}
                />
                <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 -70px 90px -50px rgba(12,10,9,0.95)" }} />
              </motion.div>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 0.8 }} className="text-center mt-4 text-[0.65rem] tracking-[0.4em] uppercase text-gold/70" data-testid="hero-bottle-caption">
                {L.hero.bottle}
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* FEATURED */}
      <section className="py-24 lg:py-32" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Reveal><p className="eyebrow">{L.featured.eyebrow}</p></Reveal>
              <Reveal delay={0.08}><h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ivory mt-4">{L.featured.title}</h2></Reveal>
              <Reveal delay={0.14}><p className="text-sand mt-3 text-sm lg:text-base max-w-lg">{L.featured.sub}</p></Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link to="/shop" data-testid="featured-cta" className="btn-ghost inline-flex items-center gap-2 rounded-full px-7 py-3 text-[0.68rem] tracking-[0.25em] uppercase">
                {L.featured.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-9 mt-14">
            {featured.map((p, i) => (
              <ProductCard key={p.slug} p={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      <FamilyStaircase />

      {/* COLLECTIONS BENTO */}
      <section className="py-24 lg:py-32 border-t border-gold/15" data-testid="collections-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><p className="eyebrow">{L.collections.eyebrow}</p></Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-4">
            <Reveal delay={0.08}><h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ivory">{L.collections.title}</h2></Reveal>
            <Reveal delay={0.14}><p className="text-sand text-sm lg:text-base md:text-right max-w-sm">{L.collections.sub}</p></Reveal>
          </div>
          <div className="grid md:grid-cols-2 gap-5 lg:gap-7 mt-14 lg:h-[640px]">
            {[
              { key: "classiques", img: products.find((p) => p.slug === "misk-al-shoyoukh")?.image, span: "md:row-span-2 h-[380px] md:h-full" },
              { key: "melanges", img: products.find((p) => p.slug === "musc-oud")?.image, span: "h-[380px]" },
              { key: "fruite", img: products.find((p) => p.slug === "musc-tahara-ananas")?.image, span: "h-[380px]" },
            ].map((c, i) => {
              const col = L.collections[c.key];
              return (
                <Reveal key={c.key} delay={0.08 * i} className={c.span}>
                  <Link
                    to={`/shop?collection=${c.key}`}
                    data-testid={"collection-card-" + c.key}
                    className="relative block h-full w-full overflow-hidden rounded-2xl border border-gold/20 group"
                  >
                    <img src={c.img} alt={col.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.05]" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(12,10,9,0.96) 12%, rgba(12,10,9,0.35) 55%, rgba(12,10,9,0.15))" }} />
                    <div className="relative h-full flex flex-col justify-end p-7 lg:p-9">
                      <h3 className="font-display text-2xl lg:text-3xl text-ivory">{col.title}</h3>
                      <p className="text-ivory/75 text-sm mt-2 max-w-sm leading-relaxed">{col.desc}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-[0.68rem] tracking-[0.25em] uppercase text-gold">
                        {col.cta} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* STORY TEASER */}
      <section className="py-24 lg:py-32 border-t border-gold/15" data-testid="story-teaser-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Reveal><p className="eyebrow">{L.storyTeaser.eyebrow}</p></Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ivory mt-4 whitespace-pre-line">{L.storyTeaser.title}</h2>
            </Reveal>
            <Reveal delay={0.16}><p className="text-sand mt-6 text-sm lg:text-base leading-relaxed max-w-md">{L.storyTeaser.body}</p></Reveal>
            <Reveal delay={0.24}>
              <Link to="/story" data-testid="story-teaser-cta" className="btn-ghost inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[0.68rem] tracking-[0.25em] uppercase mt-9">
                {L.storyTeaser.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="relative overflow-hidden rounded-2xl border border-gold/20">
              <motion.img style={{ y: archesY }} src={ARCHES_IMG} alt="Architecture arabe" className="w-full h-[340px] lg:h-[460px] object-cover" />
              <div className="absolute inset-0" style={{ boxShadow: "inset 0 -80px 100px -60px rgba(12,10,9,0.9)" }} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* MISSION TEASER */}
      <section className="relative py-24 lg:py-32 border-t border-gold/15 bg-onyx star-lattice" data-testid="mission-teaser-section">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <Reveal>
            <PalestineFlag className="w-8 h-5 rounded-[3px] mx-auto" />
            <p className="eyebrow mt-6">{L.missionTeaser.eyebrow}</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ivory mt-4 whitespace-pre-line">{L.missionTeaser.title}</h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-sand mt-6 text-sm lg:text-base leading-relaxed" data-testid="mission-teaser-body">{L.missionTeaser.body}</p>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="mt-10 inline-flex flex-col items-center border border-gold/30 rounded-2xl px-10 py-8 bg-obsidian/60" data-testid="mission-stat">
              <span className="font-display text-5xl lg:text-6xl text-gold">{L.missionTeaser.stat}</span>
              <span className="text-ivory/80 text-xs lg:text-sm mt-3 max-w-xs leading-relaxed">{L.missionTeaser.statLabel}</span>
            </div>
          </Reveal>
          <Reveal delay={0.28}>
            <Link to="/mission" data-testid="mission-teaser-cta" className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-[0.7rem] tracking-[0.25em] uppercase font-semibold mt-10">
              {L.missionTeaser.cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WHY */}
      <section className="py-24 lg:py-32 border-t border-gold/15" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><p className="eyebrow text-center">{L.why.eyebrow}</p></Reveal>
          <Reveal delay={0.08}><h2 className="font-display text-3xl sm:text-4xl text-ivory text-center mt-4">{L.why.title}</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7 mt-14">
            {L.why.items.map((w, i) => {
              const Icon = whyIcons[i];
              return (
                <Reveal key={i} delay={0.08 * i} className="h-full">
                  <div className="h-full flex flex-col items-start gap-4 border border-gold/20 rounded-2xl p-7 bg-onyx/50 hover:border-gold/50 transition-colors duration-500" data-testid={"why-item-" + i}>
                    <span className="w-11 h-11 rounded-full border border-gold/40 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gold" strokeWidth={1.4} />
                    </span>
                    <p className="text-ivory/90 text-sm leading-relaxed">{w}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

    </div>
  );
}
